import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { 
  BuildingOfficeIcon, 
  UserGroupIcon, 
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  CogIcon,
  ChartBarIcon,
  SparklesIcon,
  CalendarDaysIcon,
  RectangleStackIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

// Import existing CRM pages as components
import { CRMPage } from './CRMPage';
import { ContactsPage } from './ContactsPage';
import { InteractionsPage } from './InteractionsPage';
import { EmailCampaignsPage } from './EmailCampaignsPage';
import { EmailTemplatesPage } from './EmailTemplatesPage';
import { EmailSettingsPage } from './EmailSettingsPage';
import { CRMDashboard } from './CRMDashboard';
import { ConversationalCRMPage } from './ConversationalCRMPage';
import { OutboundTodayPage } from '@/pages/outbound/OutboundTodayPage';
import { OutboundWorkflowsPage } from '@/pages/outbound/OutboundWorkflowsPage';
import { OutboundImportPage } from '@/pages/outbound/OutboundImportPage';

interface Tab {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<any>;
  visible: boolean;
}

export const UnifiedCRMPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useAuth();
  const { t } = useLanguage();
  
  // Redirect if trying to access conversational CRM (hidden tab)
  React.useEffect(() => {
    if (id === 'conversational') {
      navigate('/crm/dashboard', { replace: true });
    }
  }, [id, navigate]);
  
  const [activeTab, setActiveTab] = useState<string>(id || 'today');

  // Define all CRM tabs
  const allTabs: Tab[] = useMemo(() => [
    {
      id: 'today',
      name: t('crm.today') || 'Today',
      icon: CalendarDaysIcon,
      component: OutboundTodayPage,
      visible: true
    },
    {
      id: 'workflows',
      name: t('crm.workflows') || 'Workflows',
      icon: RectangleStackIcon,
      component: OutboundWorkflowsPage,
      visible: true
    },
    {
      id: 'import',
      name: t('crm.import') || 'Import',
      icon: ArrowUpTrayIcon,
      component: OutboundImportPage,
      visible: true
    },
    {
      id: 'dashboard',
      name: t('crm.dashboard') || 'Dashboard',
      icon: ChartBarIcon,
      component: CRMDashboard,
      visible: true
    },
    {
      id: 'companies',
      name: t('crm.companies') || 'Companies',
      icon: BuildingOfficeIcon,
      component: CRMPage,
      visible: true
    },
    {
      id: 'contacts',
      name: t('crm.contacts') || 'Contacts',
      icon: UserGroupIcon,
      component: ContactsPage,
      visible: true
    },
    {
      id: 'interactions',
      name: t('crm.interactions') || 'Interactions',
      icon: ChatBubbleLeftRightIcon,
      component: InteractionsPage,
      visible: true
    },
    {
      id: 'campaigns',
      name: t('crm.campaigns') || 'Email Campaigns',
      icon: EnvelopeIcon,
      component: EmailCampaignsPage,
      visible: true
    },
    {
      id: 'templates',
      name: t('crm.templates') || 'Email Templates',
      icon: DocumentTextIcon,
      component: EmailTemplatesPage,
      visible: true
    },
    {
      id: 'settings',
      name: t('crm.settings') || 'Email Settings',
      icon: CogIcon,
      component: EmailSettingsPage,
      visible: true
    },
    {
      id: 'conversational',
      name: t('crm.conversational') || 'Conversational CRM',
      icon: SparklesIcon,
      component: ConversationalCRMPage,
      visible: false // Hidden tab
    }
  ], [t]);

  // Get visible tabs based on user role
  const getVisibleTabs = () => {
    return allTabs.filter(tab => tab.visible);
  };

  const visibleTabs = getVisibleTabs();

  React.useEffect(() => {
    if (id && visibleTabs.some((tab) => tab.id === id) && id !== activeTab) {
      setActiveTab(id);
    }
  }, [id, visibleTabs, activeTab]);

  const currentTab = visibleTabs.find(tab => tab.id === activeTab) || visibleTabs[0];
  const CurrentComponent = currentTab?.component;

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`/crm/${tabId}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">{t('crm.title') || 'Customer Relationship Management'}</h1>
                <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
                  {t('crm.subtitle') || 'Manage your customer relationships and sales pipeline'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="tab-bar">
              <nav className="flex gap-1 overflow-x-auto items-end" aria-label="CRM tabs">
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`tab-button inline-flex items-center gap-2 ${isActive ? 'tab-button-selected' : ''}`}
                    >
                      <Icon className="h-5 w-5 shrink-0 [stroke:currentColor]" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {CurrentComponent && <CurrentComponent />}
          </div>
        </div>
      </div>
    </div>
  );
};
